import { Bookmark, ChevronRight, ExternalLink, Search } from "lucide-react";
import posthog from "posthog-js";
import React from "react";

import { Button } from "@/app/components/ui/button";
import { wrapLinksAndEmails } from "@/app/handlers/valueTransformation.js";

import { ApplicationDetails, isJobExpired, JobDetails } from "../utils/helpers";

interface ApplicationProcessProps {
	jobDetails: JobDetails;
	applicationDetails: ApplicationDetails;
	scrollToEmail: boolean;
	applicationRef: React.RefObject<HTMLDivElement>;
	onApply: (link: string | null) => void;
	onSave?: () => void;
	hasViewedExplainer?: boolean;
	hasCompletedFitCheck?: boolean;
	onExplore: () => void;
}

export function ApplicationProcess({
	jobDetails,
	applicationDetails,
	onExplore,
	applicationRef,
	onApply,
	onSave,
	hasViewedExplainer = false,
	hasCompletedFitCheck = false,
}: ApplicationProcessProps) {
	// Track apply click with context
	const handleApplyClick = (link: string | null) => {
		// Determine apply context
		const eventName = hasCompletedFitCheck
			? "apply_clicked_after_fitcheck"
			: hasViewedExplainer
				? "apply_clicked_after_explainer"
				: "apply_clicked_direct";

		posthog.capture(eventName, {
			jobId: jobDetails?._id,
			jobTitle: jobDetails?.jobTitle,
			companyName: jobDetails?.company?.company_name,
			companyId: jobDetails?.company?._id,
			hasViewedExplainer,
			hasCompletedFitCheck,
		});

		onApply(link);
	};

	if (!jobDetails?.applicationProcess || jobDetails?.applicationProcess === "" || isJobExpired(jobDetails.closeAt)) {
		return null;
	}

	return (
		<div
			// Stacked on mobile: a 6/2 split leaves the buttons too narrow to read and squeezes
			// the instructions into a column an application URL cannot fit in.
			className="bg-blue-50/50 rounded-lg p-6 mb-8 border border-blue-100 flex flex-col gap-4 md:grid md:grid-cols-8 md:gap-0"
			id="applicationProcess"
			ref={applicationRef}
		>
			{/* Commitment Zone Header */}
			{/* min-w-0 so a grid track sized in fractions can still shrink to hold the text below. */}
			<div className="md:col-span-6 min-w-0">
				<p className="text-lg font-semibold text-gray-900 mb-1">How to apply</p>

				{/* Application Instructions */}
				{/* Employers paste raw apply links here, so break inside the word — a Workday URL has
				    no space to wrap at and would otherwise run out under the buttons. */}
				<div
					className="text-gray-600 font-inter text-sm content-wrapper wrap-break-word"
					dangerouslySetInnerHTML={{
						__html: wrapLinksAndEmails(jobDetails?.applicationProcess),
					}}
				/>
			</div>

			{/* Primary Apply CTA */}
			{/* self-start so the buttons keep their own height instead of being stretched down the
			    length of the instructions beside them. */}
			<div className="flex flex-col gap-3 md:col-span-2 md:self-start">
				{applicationDetails.applicationType === "URL" && (
					<Button
						onClick={() => handleApplyClick(applicationDetails.link)}
						className="bg-primary-700 hover:bg-primary-800 text-white"
					>
						Apply for this job
						<ExternalLink className="h-4 w-4 ml-2" />
					</Button>
				)}

				{applicationDetails.applicationType === "EMAIL" && (
					<Button
						onClick={() => handleApplyClick(applicationDetails.link)}
						className="bg-primary-700 hover:bg-primary-800 text-white"
					>
						Apply for this job
						<ChevronRight className="h-4 w-4 ml-2" />
					</Button>
				)}

				{/* Secondary Actions */}
				{onSave && (
					<Button onClick={onSave} variant="secondary" size="default">
						<Bookmark className="h-4 w-4 mr-2" />
						Save for later
					</Button>
				)}

				<Button onClick={onExplore} variant="link">
					<Search className="h-4 w-4 mr-2" />
					Explore similar roles
				</Button>
			</div>
		</div>
	);
}
